<template>
  <div class="custom-dashboard">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <div>
            <div class="d-flex align-center">
              <h1 class="text-h4 font-weight-bold mr-3">
                {{ currentDashboard ? currentDashboard.name : 'Custom Dashboard' }}
              </h1>
              <v-btn
                v-if="currentDashboard"
                icon
                small
                @click="editDashboardInfo"
                :disabled="dashboardLoading"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </div>
            <p class="text-subtitle-1 text--secondary mb-0">
              {{ currentDashboard && currentDashboard.description ? currentDashboard.description : 'Customize your dashboard with drag-and-drop widgets' }}
            </p>
          </div>
          <div class="d-flex align-center gap-2">
            <v-btn
              text
              @click="$router.push('/reports')"
              :disabled="dashboardLoading"
            >
              <v-icon left>mdi-view-grid</v-icon>
              All Dashboards
            </v-btn>
            <v-btn
              color="primary"
              @click="toggleWidgetLibrary"
              :disabled="dashboardLoading"
            >
              <v-icon left>mdi-plus</v-icon>
              Add Widget
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <div v-if="dashboardLoading" class="loading-container">
      <v-row justify="center" class="mt-12">
        <v-col cols="12" class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            width="4"
            color="primary"
          ></v-progress-circular>
          <h3 class="text-h5 mt-4">Loading Dashboard...</h3>
          <p class="text-body-1">Setting up your custom dashboard</p>
        </v-col>
      </v-row>
    </div>

    <!-- Error State -->
    <div v-else-if="dashboardError" class="error-container">
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="6" class="text-center">
          <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
          <h3 class="text-h5 mt-4">Dashboard Error</h3>
          <p class="text-body-1 mb-4">{{ dashboardError }}</p>
          <v-btn color="primary" @click="retryInitialization">
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Dashboard Content -->
    <div v-else-if="currentDashboard" class="dashboard-content">
      <DashboardGrid
        :widgets="currentDashboard.widgets || []"
        :layout="currentDashboard.layout"
        @widget-updated="onWidgetUpdate"
        @widget-removed="onWidgetRemove"
        @widget-edited="handleWidgetEdit"
      />
    </div>

    <!-- Widget Library Sidebar -->
    <WidgetLibrary
      :show="showWidgetLibrary"
      :templates="widgetTemplates"
      :useDrawer="false"
      @close="showWidgetLibrary = false"
      @add-widget="handleAddWidget"
    />

    <!-- Widget Edit Modal -->
    <v-dialog v-model="showEditModal" max-width="400" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Edit Widget</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingWidgetTitle"
            label="Widget Name"
            outlined
            dense
            :rules="[v => !!v || 'Widget name is required']"
            ref="titleInput"
            @keyup.enter="saveEdit"
            @keyup.esc="cancelEdit"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelEdit">Cancel</v-btn>
          <v-btn color="primary" @click="saveEdit" :disabled="!editingWidgetTitle">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dashboard Edit Modal -->
    <v-dialog v-model="showDashboardEditModal" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Edit Dashboard</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingDashboardName"
            label="Dashboard Name"
            outlined
            dense
            :rules="[v => !!v || 'Dashboard name is required']"
            ref="dashboardNameInput"
            @keyup.enter="saveDashboardEdit"
            @keyup.esc="cancelDashboardEdit"
          ></v-text-field>
          <v-textarea
            v-model="editingDashboardDescription"
            label="Description (optional)"
            outlined
            dense
            rows="3"
            @keyup.esc="cancelDashboardEdit"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDashboardEdit">Cancel</v-btn>
          <v-btn color="primary" @click="saveDashboardEdit" :disabled="!editingDashboardName">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DashboardGrid from '@/components/dashboard/DashboardGrid.vue'
import WidgetLibrary from '@/components/dashboard/WidgetLibrary.vue'

export default {
  name: 'CustomDashboard',
  components: {
    DashboardGrid,
    WidgetLibrary
  },
  props: {
    dashboardId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showWidgetLibrary: false,
      showEditModal: false,
      showDashboardEditModal: false,
      editingWidget: null,
      editingWidgetTitle: '',
      editingDashboardName: '',
      editingDashboardDescription: ''
    }
  },
  computed: {
    currentDashboard() {
      return this.$store.getters.currentDashboard
    },
    widgetTemplates() {
      return this.$store.getters.widgetTemplates
    },
    dashboardLoading() {
      return this.$store.getters.dashboardLoading
    },
    dashboardError() {
      return this.$store.getters.dashboardError
    }
  },
  async mounted() {
    console.log('CustomDashboard: Component mounted, initializing dashboard...')
    console.log('CustomDashboard: Dashboard ID from route:', this.dashboardId)
    await this.$store.dispatch('initializeDashboard', this.dashboardId)
    console.log('CustomDashboard: Dashboard initialized')
    console.log('CustomDashboard: Current dashboard:', this.currentDashboard)
    console.log('CustomDashboard: Widget templates:', this.widgetTemplates)
    console.log('CustomDashboard: Widget templates length:', this.widgetTemplates ? this.widgetTemplates.length : 0)
    console.log('CustomDashboard: Dashboard loading:', this.dashboardLoading)
    console.log('CustomDashboard: Dashboard error:', this.dashboardError)
  },
  watch: {
    dashboardId: {
      async handler(newDashboardId) {
        if (newDashboardId) {
          console.log('CustomDashboard: Dashboard ID changed to:', newDashboardId)
          await this.$store.dispatch('switchDashboard', newDashboardId)
        }
      },
      immediate: false
    }
  },
  methods: {
    toggleWidgetLibrary() {
      console.log('CustomDashboard: Toggling widget library. Current state:', this.showWidgetLibrary)
      this.showWidgetLibrary = !this.showWidgetLibrary
      console.log('CustomDashboard: New widget library state:', this.showWidgetLibrary)
      console.log('CustomDashboard: Widget templates available:', this.widgetTemplates.length)
    },
    clearError() {
      this.$store.commit('setDashboardError', null)
    },
    async onWidgetUpdate(widgetId, updates) {
      try {
        await this.$store.dispatch('updateWidget', { widgetId, updates })
      } catch (error) {
        console.error('Error updating widget:', error)
      }
    },
    async onWidgetRemove(widgetId) {
      try {
        await this.$store.dispatch('removeWidget', widgetId)
      } catch (error) {
        console.error('Error removing widget:', error)
      }
    },
    async handleAddWidget({ templateId, position }) {
      console.log('🏠 DASHBOARD: handleAddWidget called with:', { templateId, position })
      console.log('🏠 DASHBOARD: Current dashboard:', this.currentDashboard)
      console.log('🏠 DASHBOARD: Widget templates available:', this.widgetTemplates.length)
      
      try {
        await this.$store.dispatch('addWidget', { templateId, position })
        this.showWidgetLibrary = false
        console.log('🏠 DASHBOARD: Widget added successfully, current dashboard:', this.currentDashboard)
      } catch (error) {
        console.error('🏠 DASHBOARD: Error adding widget:', error)
      }
    },
    async retryInitialization() {
      await this.$store.dispatch('initializeDashboard', this.dashboardId)
    },
    async handleWidgetEdit(widget) {
      console.log('CustomDashboard: Editing widget', widget.id)
      this.editingWidget = widget
      this.editingWidgetTitle = widget.title
      this.showEditModal = true
      
      // Focus the input field after modal opens
      this.$nextTick(() => {
        if (this.$refs.titleInput) {
          this.$refs.titleInput.focus()
        }
      })
    },
    cancelEdit() {
      this.showEditModal = false
      this.editingWidget = null
      this.editingWidgetTitle = ''
    },
    async saveEdit() {
      if (!this.editingWidgetTitle || !this.editingWidget) return
      
      try {
        console.log('CustomDashboard: Saving edited widget', this.editingWidgetTitle)
        await this.$store.dispatch('updateWidget', {
          widgetId: this.editingWidget.id,
          updates: { title: this.editingWidgetTitle }
        })
        this.showEditModal = false
        this.editingWidget = null
        this.editingWidgetTitle = ''
      } catch (error) {
        console.error('Error updating widget:', error)
        // Could add a toast notification here
      }
    },
    editDashboardInfo() {
      if (!this.currentDashboard) return
      
      this.editingDashboardName = this.currentDashboard.name
      this.editingDashboardDescription = this.currentDashboard.description || ''
      this.showDashboardEditModal = true
      
      // Focus the input field after modal opens
      this.$nextTick(() => {
        if (this.$refs.dashboardNameInput) {
          this.$refs.dashboardNameInput.focus()
        }
      })
    },
    cancelDashboardEdit() {
      this.showDashboardEditModal = false
      this.editingDashboardName = ''
      this.editingDashboardDescription = ''
    },
    async saveDashboardEdit() {
      if (!this.editingDashboardName || !this.currentDashboard) return
      
      try {
        console.log('CustomDashboard: Saving dashboard info', {
          name: this.editingDashboardName,
          description: this.editingDashboardDescription
        })
        
        await this.$store.dispatch('updateDashboardInfo', {
          dashboardId: this.currentDashboard.dashboardId,
          name: this.editingDashboardName,
          description: this.editingDashboardDescription
        })
        
        this.showDashboardEditModal = false
        this.editingDashboardName = ''
        this.editingDashboardDescription = ''
        
        // Show success message if toast is available
        if (this.$toast) {
          this.$toast.success('Dashboard updated successfully')
        }
      } catch (error) {
        console.error('Error updating dashboard:', error)
        if (this.$toast) {
          this.$toast.error('Failed to update dashboard')
        }
      }
    }
  }
}
</script>

<style scoped>
.custom-dashboard {
  padding: 24px;
  min-height: 100vh;
}

.dashboard-content {
  position: relative;
}

@media (max-width: 960px) {
  .custom-dashboard {
    padding: 16px;
  }
}
</style> 